// src/pages/tasks/new-task-page/NewTaskPage.tsx
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  SendOutlined,
  CloseOutlined,
  LeftOutlined,
} from "@ant-design/icons";
import moment, { Moment } from "moment-timezone";

const NZ_TIMEZONE = "Pacific/Auckland";
const toNZTime = (date: any) => date ? moment.utc(date).tz(NZ_TIMEZONE) : null;
import { useHistory } from "react-router-dom";
import { ApiRequest } from "../../../services/api/api";
import { getUserId } from "../../../services/lib/utils/auth.utils";
import SweetAlertService from "../../../services/lib/utils/sweet-alert-service";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

type IdLabel = { value: string; label: string };

type AssigneeRow = { userId?: string; details?: string };
type FollowerRow = { userId?: string };
type CheckpointRow = { name?: string; dueDate?: Moment | null; managerId?: string };

type NewTaskForm = {
  title: string;
  description?: string;
  templateId?: string;
  managerId?: string;
  assignees: AssigneeRow[];
  followers: FollowerRow[];
  startDate?: Moment | null;
  dueDate?: Moment | null;
  checkpoints: CheckpointRow[];
};

const USE_MOCK = false;

/* -------------------- Mock & API placeholders -------------------- */
async function apiSearchUsers(keyword: string, cache: IdLabel[]): Promise<IdLabel[]> {
  if (USE_MOCK) {
    const all = [
      { value: "u1", label: "Alice Johnson" },
      { value: "u2", label: "Bob Lee" },
      { value: "u3", label: "Chris Wang" },
      { value: "u4", label: "Diana Smith" },
      { value: "u5", label: "Eric Zhao" },
    ];
    const k = (keyword || "").toLowerCase();
    return all.filter((x) => x.label.toLowerCase().includes(k));
  }
  const k = (keyword || "").toLowerCase();
  return cache.filter((x) => x.label.toLowerCase().includes(k));
}

async function apiListTemplates(): Promise<IdLabel[]> {
  if (USE_MOCK) {
    return [
      { value: "t1", label: "Quotation Template" },
      { value: "t2", label: "Onboarding Checklist" },
    ];
  }
  const result = await ApiRequest({
    otherUrl: "TaskTemplate/GetAll",
    type: "get",
  });
  const list = result?.data?.data ?? result?.data ?? [];
  return (list || []).map((tpl: any) => ({
    value: String(tpl.templateId ?? tpl.TemplateId ?? tpl.id ?? tpl.Id ?? ""),
    label: tpl.name ?? tpl.Name ?? tpl.title ?? tpl.Title ?? "Template",
  }));
}

/** 取模板详情并返回可直接 setFieldsValue 的结构 */
async function apiGetTemplateDetail(templateId: string) {
  if (USE_MOCK) {
    // 演示：不同模板给出不同预置
    if (templateId === "t2") {
      return {
        title: "New Hire Onboarding",
        description: "Welcome pack, account provisioning, induction.",
        managerId: "u1",
        assignees: [
          { userId: "u2", details: "Prepare laptop and accessories" },
          { userId: "u3", details: "Create accounts & permissions" },
        ],
        followers: [{ userId: "u4" }],
        startDate: moment().tz(NZ_TIMEZONE),
        dueDate: moment().tz(NZ_TIMEZONE).add(7, "day"),
        checkpoints: [
          { name: "Equipment Ready", dueDate: moment().tz(NZ_TIMEZONE).add(2, "day") },
          { name: "Accounts Created", dueDate: moment().tz(NZ_TIMEZONE).add(3, "day") },
          { name: "Induction Completed", dueDate: moment().tz(NZ_TIMEZONE).add(6, "day") },
        ],
      } as Partial<NewTaskForm>;
    }
    return {
      title: "Quotation Follow-up",
      description: "Track quotation and confirm with customer.",
      managerId: "u1",
      assignees: [{ userId: "u2", details: "Send quotation and follow up" }],
      followers: [{ userId: "u3" }],
      startDate: moment().tz(NZ_TIMEZONE),
      dueDate: moment().tz(NZ_TIMEZONE).add(5, "day"),
      checkpoints: [
        { name: "Send Quote", dueDate: moment().tz(NZ_TIMEZONE).add(1, "day") },
        { name: "Customer Reply", dueDate: moment().tz(NZ_TIMEZONE).add(3, "day") },
        { name: "Confirm Order", dueDate: moment().tz(NZ_TIMEZONE).add(5, "day") },
      ],
    } as Partial<NewTaskForm>;
  }
  const result = await ApiRequest({
    otherUrl: "TaskTemplate/GetById",
    type: "get",
    params: { templateId },
  });
  const data = result?.data?.data ?? result?.data ?? {};

  // Some template records store full template payload as JSON string in description.
  // Parse it and prefer structured fields to avoid dumping all content into Description.
  const rawDescription = data?.description ?? data?.Description;
  let embeddedTemplate: any = null;
  if (typeof rawDescription === "string") {
    const text = rawDescription.trim();
    if (text.startsWith("{")) {
      try {
        embeddedTemplate = JSON.parse(text);
      } catch {
        embeddedTemplate = null;
      }
    }
  }
  const source = embeddedTemplate && typeof embeddedTemplate === "object" ? embeddedTemplate : data;

  const assigneeRows = Array.isArray(source?.assignees)
    ? source.assignees
    : Array.isArray(source?.Assignees)
    ? source.Assignees
    : Array.isArray(source?.assigneeIds)
    ? source.assigneeIds.map((id: any) => ({ userId: id, details: "" }))
    : [];

  const followerRows = Array.isArray(source?.followers)
    ? source.followers
    : Array.isArray(source?.Followers)
    ? source.Followers
    : Array.isArray(source?.followerIds)
    ? source.followerIds.map((id: any) => ({ userId: id }))
    : [];

  const checkpointRows = Array.isArray(source?.checkpoints)
    ? source.checkpoints
    : Array.isArray(source?.Checkpoints)
    ? source.Checkpoints
    : [];

  return {
    title: source?.title ?? source?.Title ?? data?.title ?? data?.Title ?? "",
    description:
      source?.description ??
      source?.Description ??
      (embeddedTemplate ? "" : (data?.description ?? data?.Description ?? "")),
    managerId: (source?.managerId ?? source?.ManagerId)
      ? String(source?.managerId ?? source?.ManagerId)
      : undefined,
    assignees: assigneeRows.map((a: any) => ({
      userId: (a?.userId ?? a?.UserId) ? String(a?.userId ?? a?.UserId) : undefined,
      details: a?.details ?? a?.Details ?? "",
    })),
    followers: followerRows.map((f: any) => ({
      userId: (f?.userId ?? f?.UserId) ? String(f?.userId ?? f?.UserId) : undefined,
    })),
    startDate: (source?.startDate ?? source?.StartDate) ? toNZTime(source?.startDate ?? source?.StartDate) : undefined,
    dueDate: (source?.dueDate ?? source?.DueDate) ? toNZTime(source?.dueDate ?? source?.DueDate) : undefined,
    checkpoints: checkpointRows.map((c: any) => ({
      name: c?.name ?? c?.Name,
      dueDate: (c?.dueDate ?? c?.DueDate) ? toNZTime(c?.dueDate ?? c?.DueDate) : undefined,
      managerId: (c?.managerId ?? c?.ManagerId) ? String(c?.managerId ?? c?.ManagerId) : undefined,
    })),
  } as Partial<NewTaskForm>;
}

async function apiCreateTask(payload: any) {
  if (USE_MOCK) {
    console.log("[MOCK create task]", payload);
    await new Promise((r) => setTimeout(r, 400));
    return { id: String(1000 + Math.floor(Math.random() * 1000)) };
  }
  const result = await ApiRequest({
    otherUrl: "TaskManagement/AddTask",
    method: "post",
    data: payload,
    isShowSpinner: true,
  });
  return result?.data?.data ?? result?.data;
}

async function apiSaveTemplate(payload: any) {
  if (USE_MOCK) {
    console.log("[MOCK save template]", payload);
    await new Promise((r) => setTimeout(r, 400));
    return { templateId: String(1000 + Math.floor(Math.random() * 1000)) };
  }
  const result = await ApiRequest({
    otherUrl: "TaskTemplate/AddTemplate",
    method: "post",
    data: payload,
    isShowSpinner: true,
  });
  return result?.data?.data ?? result?.data;
}

async function apiDeleteTemplate(templateId: string) {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 200));
    return true;
  }
  const result = await ApiRequest({
    otherUrl: "TaskTemplate/DeleteTemplate",
    method: "delete",
    params: { templateId: parseInt(String(templateId), 10) },
    isShowSpinner: true,
  });
  return result?.data?.data ?? result?.data ?? true;
}
/* ---------------------------------------------------------------- */

const NewTaskPage: React.FC = () => {
  const history = useHistory();
  const [form] = Form.useForm<NewTaskForm>();

  const [userOptions, setUserOptions] = useState<IdLabel[]>([]);
  const [allUsers, setAllUsers] = useState<IdLabel[]>([]);
  const [templates, setTemplates] = useState<IdLabel[]>([]);
  const [saving, setSaving] = useState(false);
  
  // 监听 DueDate 和 StartDate 变化
  const dueDate = Form.useWatch("dueDate", form);
  const startDate = Form.useWatch("startDate", form);

  useEffect(() => {
    document.title = "New Task";
    // 默认值
    form.setFieldsValue({
      assignees: [{}],             // 默认只显示1行
      followers: [{}, {}],
      checkpoints: [
        { name: "Checkpoint A" },
        { name: "Checkpoint B" },
        { name: "final due date" },
      ],
      startDate: moment().tz(NZ_TIMEZONE),
    });
    apiListTemplates().then(setTemplates);
    loadEmployees();
  }, [form]);

  const loadEmployees = async () => {
    if (USE_MOCK) {
      const mock = await apiSearchUsers("", []);
      setAllUsers(mock);
      setUserOptions(mock);
      return;
    }
    try {
      const result = await ApiRequest({
        otherUrl: "Employee/GetAllEmployee",
        type: "get",
      });
      const employees = result?.data?.data ?? result?.data ?? [];
      const options: IdLabel[] = (employees || []).map((emp: any) => {
        const name = `${emp.firstName ?? ""} ${emp.lastName ?? ""}`.trim();
        return {
          value: String(emp.employeeId ?? emp.EmployeeId),
          label: name || emp.userName || emp.UserName || `Employee ${emp.employeeId ?? emp.EmployeeId}`,
        };
      });
      setAllUsers(options);
      setUserOptions(options);
    } catch (error: any) {
      message.error(error?.message || "Failed to load employees");
    }
  };

  const onUserSearch = async (kw: string) => {
    const opts = await apiSearchUsers(kw, allUsers);
    setUserOptions(opts);
  };

  const onUserFocus = () => {
    setUserOptions(allUsers);
  };

  /** 选择 DueDate 时只更新 "final due date" checkpoint 的日期 */
  const fillDueForCheckpoints = (dd?: Moment | null) => {
    const cps: CheckpointRow[] = form.getFieldValue("checkpoints") || [];
    const next = cps.map((c) => {
      // 只更新 "final due date" checkpoint 的日期
      if (c.name && c.name.toLowerCase() === "final due date") {
        return { ...c, dueDate: dd || null };
      }
      return c;
    });
    form.setFieldsValue({ checkpoints: next });
  };

  /** 根据日期自动排序 checkpoints（排除 "final due date"） */
  const sortCheckpointsByDate = () => {
    const cps: CheckpointRow[] = form.getFieldValue("checkpoints") || [];
    if (!cps || cps.length === 0) return;
    
    const finalDueDateCheckpoint = cps.find(
      (c) => c.name && c.name.toLowerCase() === "final due date"
    );
    const otherCheckpoints = cps.filter(
      (c) => !c.name || c.name.toLowerCase() !== "final due date"
    );

    // 对普通 checkpoints 按日期排序
    const sorted = [...otherCheckpoints].sort((a, b) => {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1; // 没有日期的排在后面
      if (!b.dueDate) return -1;
      return a.dueDate.isBefore(b.dueDate) ? -1 : 1;
    });

    // 重新组合：排序后的普通 checkpoints + "final due date"（如果有）
    const result = [...sorted];
    if (finalDueDateCheckpoint) {
      result.push(finalDueDateCheckpoint);
    }

    // 只有当顺序真的改变时才更新表单，避免无限循环
    const currentOrder = cps.map((c, idx) => ({ name: c.name, idx })).map((x) => x.name).join(",");
    const newOrder = result.map((c) => c.name).join(",");
    if (currentOrder !== newOrder) {
      form.setFieldsValue({ checkpoints: result });
    }
  };

  /** 获取 checkpoint DatePicker 的 disabledDate 函数 */
  const getCheckpointDisabledDate = (current: Moment | null) => {
    if (!current) return false;
    if (startDate && current.isBefore(startDate, "day")) {
      return true; // 早于开始日期，禁用
    }
    if (dueDate && current.isAfter(dueDate, "day")) {
      return true; // 晚于结束日期，禁用
    }
    return false;
  };

  // 监听 DueDate 变化，自动更新 "final due date" checkpoint
  useEffect(() => {
    if (dueDate) {
      fillDueForCheckpoints(dueDate);
    }
  }, [dueDate]);

  // 当开始/结束日期变化时，重新排序 checkpoints
  useEffect(() => {
    const timer = setTimeout(() => {
      sortCheckpointsByDate();
    }, 100);
    return () => clearTimeout(timer);
  }, [startDate?.format("YYYY-MM-DD"), dueDate?.format("YYYY-MM-DD")]);

  /** 套用模板：把模板字段直接灌入表单 */
  const applyTemplate = async (templateId?: string) => {
    if (!templateId) return;
    try {
      const t = await apiGetTemplateDetail(templateId);
      // 注意：这里直接覆盖相同字段
      // 处理 checkpoints：确保有 "final due date"
      let templateCheckpoints = t.checkpoints && t.checkpoints.length ? [...t.checkpoints] : [{ name: "Checkpoint A" }];
      // 检查是否已有 "final due date"
      const hasFinalDueDate = templateCheckpoints.some(
        (c: any) => c.name && c.name.toLowerCase() === "final due date"
      );
      // 如果没有，添加 "final due date"
      if (!hasFinalDueDate) {
        templateCheckpoints.push({ name: "final due date", dueDate: t.dueDate || null });
      }
      
      form.setFieldsValue({
        title: t.title,
        description: t.description,
        managerId: t.managerId,
        assignees: t.assignees || [{}],
        followers: t.followers || [{}],
        startDate: t.startDate,
        dueDate: t.dueDate,
        checkpoints: templateCheckpoints,
      });
      // 模板如果带了 dueDate，也同步更新 "final due date" checkpoint
      fillDueForCheckpoints(t.dueDate || null);
      message.success("Template applied");
    } catch (e: any) {
      message.error(e?.message || "Failed to apply template");
    }
  };

  const onCreate = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);

      const payload = {
        title: (v.title || "").trim(),
        description: v.description || "",
        templateId: v.templateId || null,
        creatorId: USE_MOCK ? 1 : parseInt(String(getUserId() || ""), 10),
        managerId: v.managerId ? parseInt(String(v.managerId), 10) : null,
        assignees: (v.assignees || [])
          .filter((a) => a?.userId)
          .map((a) => ({
            userId: parseInt(String(a.userId), 10),
            details: a.details || "",
          })),
        assigneeIds: (v.assignees || [])
          .map((a) => (a?.userId ? parseInt(String(a.userId), 10) : undefined))
          .filter((id): id is number => !!id),
        followerIds: (v.followers || [])
          .map((f) => (f?.userId ? parseInt(String(f.userId), 10) : undefined))
          .filter((id): id is number => !!id),
        startDate: v.startDate ? v.startDate.toISOString() : null,
        dueDate: v.dueDate ? v.dueDate.toISOString() : null,
        checkpoints: (v.checkpoints || [])
          .filter((c) => c.name && c.name.toLowerCase() !== "final due date")
          .map((c) => ({
            name: c.name,
            dueDate: c.dueDate ? c.dueDate.toISOString() : null,
            managerId: c.managerId ? parseInt(String(c.managerId), 10) : null,
          })),
      };

      const resultId = await apiCreateTask(payload);
      const newTaskId = resultId?.id ?? resultId ?? null;

      message.success("Task created");
      // 跳转到任务列表页面
      history.push("/tasks");
    } catch (e: any) {
      if (e?.errorFields) return; // 表单校验已提示
      message.error(e?.message || "Failed to create task");
    } finally {
      setSaving(false);
    }
  };

  const onSaveTemplate = async () => {
    try {
      const v = await form.validateFields();
      
      // 弹出输入框让用户输入模板名称
      const templateName = await SweetAlertService.inputConfirm({
        type: "text",
        title: "Save as Template",
        textLabel: "Please enter a name for this template:",
        placeholder: "Template Name",
        defaultValue: v.title || "",
      });

      if (!templateName) {
        return; // 用户取消或未输入
      }

      setSaving(true);

      const createdBy = USE_MOCK ? 1 : parseInt(String(getUserId() || ""), 10);
      if (!createdBy || isNaN(createdBy)) {
        message.error("User not identified");
        setSaving(false);
        return;
      }

      const payload = {
        name: templateName.trim(),
        title: (v.title || "").trim(),
        description: v.description || "",
        managerId: v.managerId ? parseInt(String(v.managerId), 10) : null,
        // Template should keep content/people only, not concrete schedule dates.
        startDate: null,
        dueDate: null,
        createdBy: createdBy,
        assignees: (v.assignees || [])
          .filter((a) => a?.userId)
          .map((a) => ({
            userId: parseInt(String(a.userId), 10),
            details: a.details || "",
          })),
        followers: (v.followers || [])
          .filter((f) => f?.userId)
          .map((f) => ({
            userId: parseInt(String(f.userId), 10),
            details: "",
          })),
        checkpoints: (v.checkpoints || [])
          .filter((c) => c.name && c.name.toLowerCase() !== "final due date")
          .map((c) => ({
            checkpointId: 0,
            name: c.name,
            dueDate: null,
            managerId: c.managerId ? parseInt(String(c.managerId), 10) : null,
          })),
      };

      await apiSaveTemplate(payload);
      message.success("Template saved successfully");
      
      // 刷新模板列表
      apiListTemplates().then(setTemplates);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const onDeleteTemplate = async (templateId: string, templateName: string) => {
    const confirmed = await SweetAlertService.confirmMessage(
      `Delete template "${templateName}"?`
    );
    if (!confirmed) return;

    try {
      setSaving(true);
      await apiDeleteTemplate(templateId);
      const nextTemplates = await apiListTemplates();
      setTemplates(nextTemplates);

      if (form.getFieldValue("templateId") === templateId) {
        form.setFieldsValue({ templateId: undefined });
      }

      message.success("Template deleted");
    } catch (e: any) {
      message.error(e?.message || "Failed to delete template");
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <Space>
      <Button icon={<SaveOutlined />} loading={saving} onClick={onSaveTemplate}>
        Save as Template
      </Button>
      <Button type="primary" loading={saving} icon={<SendOutlined />} onClick={onCreate}>
        Create
      </Button>
    </Space>
  );

  return (
    <div style={{ padding: 24, background: "#f7f9fc", minHeight: "100vh" }}>
      <Card
        bordered={false}
        style={{ maxWidth: 1100, margin: "0 auto", boxShadow: "0 2px 10px rgba(0,0,0,.06)" }}
        bodyStyle={{ padding: 24 }}
      >
        <Row justify="space-between" align="middle" wrap={false}>
          <Col>
            <Space direction="vertical" size={2}>
              <Button
                type="link"
                icon={<LeftOutlined />}
                onClick={() => history.push("/tasks")}
                style={{ paddingLeft: 0, height: "auto" }}
              >
                Back to List
              </Button>
              <Title level={3} style={{ margin: 0 }}>New Task</Title>
              <Text type="secondary">Create a task or start from a template</Text>
            </Space>
          </Col>
          <Col>
            <Space>
              <Text type="secondary">Create From a Template</Text>
              <Select
                allowClear
                style={{ width: 260 }}
                placeholder="Select template"
                onChange={(tid) => {
                  form.setFieldsValue({ templateId: tid });
                  applyTemplate(tid as string | undefined);
                }}
              >
                {templates.map((tpl) => (
                  <Option key={tpl.value} value={tpl.value}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {tpl.label}
                      </span>
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined style={{ color: "#ff4d4f" }} />}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onDeleteTemplate(tpl.value, tpl.label);
                        }}
                        style={{ flexShrink: 0, padding: 0, width: 20, height: 20 }}
                      />
                    </div>
                  </Option>
                ))}
              </Select>
            </Space>
          </Col>
        </Row>

        <Divider />

        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={16}>
              <Form.Item
                name="title"
                label="Title"
                rules={[{ required: true, message: "Please enter a title" }]}
              >
                <Input placeholder="Task title" maxLength={120} />
              </Form.Item>

              <Form.Item name="description" label="Task Description">
                <TextArea rows={5} placeholder="Describe the task…" />
              </Form.Item>
            </Col>

            <Col xs={24} md={8}>
              <Form.Item
                name="managerId"
                label="Task Manager"
                rules={[{ required: true, message: "Please select a manager" }]}
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Select manager"
                  onSearch={onUserSearch}
                  onFocus={onUserFocus}
                  filterOption={false}
                  options={userOptions}
                />
              </Form.Item>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item name="startDate" label="Start Date">
                    <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="dueDate" label="Due Date">
                    <DatePicker
                      style={{ width: "100%" }}
                      format="YYYY-MM-DD"
                      onChange={fillDueForCheckpoints}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Assignees with execution details */}
          <Card size="small" title="Assignees & Execution Details" style={{ marginTop: 8 }}>
            <Form.List name="assignees">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Row key={field.key} gutter={8} align="top" style={{ marginBottom: 8 }}>
                      <Col xs={24} md={10}>
                        <Form.Item
                          {...field}
                          name={[field.name, "userId"]}
                          fieldKey={[field.fieldKey!, "userId"]}
                          rules={[{ required: true, message: "Please select an assignee" }]}
                          label={field.name === 0 ? "Assignee" : undefined}
                        >
                          <Select
                            showSearch
                            allowClear
                            placeholder="Select assignee"
                            onSearch={onUserSearch}
                            onFocus={onUserFocus}
                            filterOption={false}
                            options={userOptions}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={13}>
                        <Form.Item
                          {...field}
                          name={[field.name, "details"]}
                          fieldKey={[field.fieldKey!, "details"]}
                          label={field.name === 0 ? "Execution Details" : undefined}
                        >
                          <TextArea autoSize={{ minRows: 1, maxRows: 4 }} placeholder="What to do / notes…" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={1} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                        {fields.length > 1 && (
                          <Button
                            type="text"
                            icon={<CloseOutlined style={{ color: "#000", fontSize: "16px" }} />}
                            onClick={() => remove(field.name)}
                            style={{
                              background: "transparent",
                              border: "none",
                              padding: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginTop: field.name === 0 ? "25px" : "0",
                            }}
                          />
                        )}
                      </Col>
                    </Row>
                  ))}
                  <Button icon={<PlusOutlined />} onClick={() => add({})}>
                    Add Assignee
                  </Button>
                </>
              )}
            </Form.List>
          </Card>

          {/* Followers */}
          <Card size="small" title="Followers" style={{ marginTop: 12 }}>
            <Form.List name="followers">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Row key={field.key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                      <Col xs={22} md={11}>
                        <Form.Item
                          {...field}
                          name={[field.name, "userId"]}
                          fieldKey={[field.fieldKey!, "userId"]}
                        >
                          <Select
                            showSearch
                            allowClear
                            placeholder="Select follower"
                            onSearch={onUserSearch}
                            onFocus={onUserFocus}
                            filterOption={false}
                            options={userOptions}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={2} md={1} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                        {fields.length > 1 && (
                          <Button
                            type="text"
                            icon={<CloseOutlined style={{ color: "#000", fontSize: "16px" }} />}
                            onClick={() => remove(field.name)}
                            style={{
                              background: "transparent",
                              border: "none",
                              padding: "4px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginLeft: "-10px",
                              marginTop: field.name === 0 ? "-20px" : "-10px",
                            }}
                          />
                        )}
                      </Col>
                    </Row>
                  ))}
                  <Button icon={<PlusOutlined />} onClick={() => add({})}>
                    Add Follower
                  </Button>
                </>
              )}
            </Form.List>
          </Card>

          {/* Checkpoints */}
          <Card size="small" title="Checkpoints" style={{ marginTop: 12 }}>
            <Form.List name="checkpoints">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, idx) => {
                    const checkpointName = form.getFieldValue(["checkpoints", field.name, "name"]);
                    const isFinalDueDate = checkpointName && 
                      checkpointName.toLowerCase() === "final due date";
                    
                    // 如果是 "final due date"，确保日期与 DueDate 同步
                    if (isFinalDueDate && dueDate) {
                      const currentCheckpointDate = form.getFieldValue(["checkpoints", field.name, "dueDate"]);
                      if (!currentCheckpointDate || !moment(currentCheckpointDate).isSame(dueDate, "day")) {
                        form.setFieldValue(["checkpoints", field.name, "dueDate"], dueDate);
                      }
                    }
                    
                    return (
                      <Row key={field.key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                        <Col xs={24} md={7}>
                          <Form.Item
                            {...field}
                            name={[field.name, "name"]}
                            fieldKey={[field.fieldKey!, "name"]}
                            label={idx === 0 ? "Checkpoint" : undefined}
                            rules={[{ required: true, message: "Please input a checkpoint name" }]}
                          >
                            <Input 
                              placeholder="e.g. Checkpoint A" 
                              disabled={isFinalDueDate}
                              style={isFinalDueDate ? { backgroundColor: "#f5f5f5" } : {}}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={20} md={6}>
                          <Form.Item
                            {...field}
                            name={[field.name, "dueDate"]}
                            fieldKey={[field.fieldKey!, "dueDate"]}
                            label={idx === 0 ? "Due Date" : undefined}
                          >
                            <DatePicker 
                              style={{ width: "100%" }} 
                              format="YYYY-MM-DD"
                              disabled={isFinalDueDate}
                              value={isFinalDueDate ? dueDate : undefined}
                              disabledDate={getCheckpointDisabledDate}
                              onChange={(date) => {
                                // 当日期改变时，触发排序
                                setTimeout(() => {
                                  sortCheckpointsByDate();
                                }, 50);
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={22} md={9}>
                          <Form.Item
                            {...field}
                            name={[field.name, "managerId"]}
                            fieldKey={[field.fieldKey!, "managerId"]}
                            label={idx === 0 ? "Checkpoint Owner" : undefined}
                          >
                            <Select
                              showSearch
                              allowClear
                              placeholder="Select checkpoint owner"
                              onSearch={onUserSearch}
                              onFocus={onUserFocus}
                              filterOption={false}
                              options={userOptions}
                              disabled={isFinalDueDate}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={4} md={2} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                          {fields.length > 1 && !isFinalDueDate && (
                            <Button
                              type="text"
                              icon={<CloseOutlined style={{ color: "#000", fontSize: "16px" }} />}
                              onClick={() => remove(field.name)}
                              style={{
                                background: "transparent",
                                border: "none",
                                padding: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginLeft: "-30px",
                                marginTop: idx === 1 ? "-15px" : "0",
                              }}
                            />
                          )}
                        </Col>
                      </Row>
                    );
                  })}
                  <Button 
                    icon={<PlusOutlined />} 
                    onClick={() => {
                      add({});
                      // 添加后延迟排序，确保新 checkpoint 已添加到表单
                      setTimeout(() => {
                        sortCheckpointsByDate();
                      }, 100);
                    }}
                  >
                    Add Checkpoint
                  </Button>
                </>
              )}
            </Form.List>
          </Card>

          <Divider />

          <Row justify="end">
            <Space>
              {footer}
            </Space>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default NewTaskPage;
